import React, { useMemo } from "react";
import {
  computeDailyWorkUpdateStats,
  computeTeamDailyWorkUpdateStats,
  getDailyWorkUpdateCompletionPercent,
} from "../../../app/utils/common/dailyWorkUpdateStats";
import "./DailyWorkUpdatesStats.scss";

const INDIVIDUAL_ITEMS = [
  { key: "workdays", label: "Work days", modifier: "workdays" },
  { key: "filled", label: "Filled", modifier: "filled" },
  { key: "missed", label: "Missed", modifier: "missed" },
  { key: "pending", label: "Pending", modifier: "pending" },
  { key: "completion", label: "Completion", modifier: "completion" },
];

const TEAM_ITEMS = [
  { key: "teamSize", label: "Team members", modifier: "workdays" },
  { key: "filled", label: "Filled", modifier: "filled" },
  { key: "missed", label: "Missed", modifier: "missed" },
  { key: "pending", label: "Pending", modifier: "pending" },
  { key: "completion", label: "Completion", modifier: "completion" },
];

const DailyWorkUpdatesStats = ({
  calendarTasks,
  calendarLeaveByDate,
  publicHolidaysByDate,
  monthDate,
  targetUserId,
  teamUserIds,
  isTeamView = false,
  employeeName = "",
  loading = false,
}) => {
  const monthLabel = monthDate
    ? monthDate.format("MMMM YYYY")
    : "This month";

  const stats = useMemo(() => {
    if (isTeamView) {
      return computeTeamDailyWorkUpdateStats({
        calendarTasks,
        calendarLeaveByDate,
        publicHolidaysByDate,
        monthDate: monthDate || undefined,
        teamUserIds,
      });
    }
    return computeDailyWorkUpdateStats({
      calendarTasks,
      calendarLeaveByDate,
      publicHolidaysByDate,
      monthDate: monthDate || undefined,
      targetUserId,
    });
  }, [
    calendarTasks,
    calendarLeaveByDate,
    publicHolidaysByDate,
    monthDate,
    targetUserId,
    teamUserIds,
    isTeamView,
  ]);

  const completionPercent = getDailyWorkUpdateCompletionPercent(stats);
  const itemDefs = isTeamView ? TEAM_ITEMS : INDIVIDUAL_ITEMS;

  const items = itemDefs.map(({ key, label, modifier }) => {
    if (key === "completion") {
      return { key, label, value: `${completionPercent}%`, modifier };
    }
    return { key, label, value: stats[key] ?? 0, modifier };
  });

  const periodSuffix = isTeamView
    ? ` · all employees (${stats.teamSize ?? 0}) this month`
    : employeeName
      ? ` · ${employeeName}`
      : "";

  return (
    <section
      className="daily-work-updates-stats"
      aria-label={`Daily work update statistics for ${monthLabel}`}
    >
      <p className="daily-work-updates-stats__period">
        {monthLabel}
        {periodSuffix}
      </p>
      <div className="daily-work-updates-stats__grid">
        {items.map(({ key, label, value, modifier }) => (
          <div
            key={key}
            className={`daily-work-updates-stats__card daily-work-updates-stats__card--${modifier} card-border card-body`}
          >
            <div className="daily-work-updates-stats__label">{label}</div>
            <div className="daily-work-updates-stats__value">
              {loading ? "…" : value}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default DailyWorkUpdatesStats;
